
import { useState } from 'react';
import { X, Heart, MessageCircle, MapPin, Clock } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

interface PostModalProps {
  post: any;
  onClose: () => void;
}

export const PostModal = ({ post, onClose }: PostModalProps) => {
  const [isLiked, setIsLiked] = useState(post.isLiked || false);

  const handleLike = () => {
    setIsLiked(!isLiked);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Post Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[60vh] p-4">
          <div className="space-y-4">
            {/* User Info */}
            <div className="flex items-start space-x-3">
              <Avatar className="w-12 h-12">
                <AvatarImage src={post.user.avatar} alt={post.user.name} />
                <AvatarFallback>{post.user.name[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <h3 className="font-semibold text-gray-900">{post.user.name}</h3>
                  {post.user.verified && (
                    <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                  <span className="text-gray-500 text-sm">@{post.user.username}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <Clock size={12} />
                  <span>{post.timestamp}</span>
                  {post.location?.address && (
                    <>
                      <span>•</span>
                      <MapPin size={12} />
                      <span>{post.location.address}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-3">
              <p className="text-gray-800 leading-relaxed">{post.content}</p>
              
              {post.whatsHappening && (
                <div className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-200">
                  <p className="text-sm text-blue-800"><strong>What's happening:</strong> {post.whatsHappening}</p>
                </div>
              )}
              
              {post.challenges && (
                <div className="p-3 bg-yellow-50 rounded-lg border-l-4 border-yellow-200">
                  <p className="text-sm text-yellow-800"><strong>Challenges:</strong> {post.challenges}</p>
                </div>
              )}

              {post.image && (
                <div className="rounded-lg overflow-hidden">
                  {post.isVideo ? (
                    <video
                      src={post.image}
                      controls
                      className="w-full max-h-96 object-cover"
                    />
                  ) : (
                    <img
                      src={post.image}
                      alt="Post content"
                      className="w-full max-h-96 object-cover"
                    />
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-6 pt-3 border-t border-gray-100">
              <button
                onClick={handleLike}
                className={`flex items-center space-x-2 transition-colors ${
                  isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                }`}
              >
                <Heart size={18} className={isLiked ? 'fill-current' : ''} />
                <span className="text-sm">{post.likes}</span>
              </button>
              <button className="flex items-center space-x-2 text-gray-500 hover:text-blue-500 transition-colors">
                <MessageCircle size={18} />
                <span className="text-sm">{post.comments}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
